import { injectable, inject, Container } from 'inversify';
import {
    FetchServiceContract,
    CONTRACTS,
    IResetSettingsModel,
    ISaveSettingsRequestModel,
    SettingsChangeServiceContract,
    ITableLayout
} from '../contracts';
import {
    ClientSettingTypeEnum,
    ContextSettings,
} from '@/dto';
import { ContextsState, SettingsState, } from '@/store';
import { TileData } from '@/core/components/tiles/types';

@injectable()
export class SettingsChangeService
    implements SettingsChangeServiceContract {

    private _fetchService: FetchServiceContract;

    constructor(
        @inject(CONTRACTS.FetchService) fetchService: FetchServiceContract
    ) {
        if (!fetchService) { throw new Error('fetchService is not set'); }

        this._fetchService = fetchService;
    }

    public async fetchSettings() {
        const loadedSettings = await this._fetchService.get<ContextSettings>(
            `/contexts/${ContextsState.currentContextId}/interface-settings`
        );
        return loadedSettings;
    }

    public async fetchSettingsForUser(userId: string) {
        const loadedSettings = await this._fetchService.get<ContextSettings>(
            `/contexts/${ContextsState.currentContextId}/interface-settings/foruser/${userId}`
        );
        return loadedSettings;
    }

    public async fetchSettingsForType(
        сlientSettingType: ClientSettingTypeEnum,
        roleName?: string): Promise<ContextSettings> {
        let url = `/contexts/${ContextsState.currentContextId}/interface-settings/${сlientSettingType}`;
        if (roleName) {
            url = url + `/${roleName}`;
        }
        const loadedSettings = await this._fetchService.get<ContextSettings>(
            url
        );

        return loadedSettings;
    }

    public async fetchTableLayoutSetting(
        settingId: number,
        key: string
    ): Promise<ITableLayout> {
        const loadedSettings = await this._fetchService.get<ITableLayout>(
            `/contexts/${ContextsState.currentContextId}/interface-settings/tablelayout/${settingId}/${key}`
        );
        return loadedSettings;
    }

    public async save(
        newData: ISaveSettingsRequestModel,
    ): Promise<boolean> {

        let result = false;
        const contextId = ContextsState.currentContextId;
        await this._fetchService.post(
            `/contexts/${contextId}/available-settings`,
            newData
        )
            .then(() => result = true)
            .catch(() => result = false);

        return result;
    }

    public async changeSetting(
        key: string,
        value: string,
        settingType?: ClientSettingTypeEnum,
        userId?: string,
        roleName?: string): Promise<any> {

        const contextId = ContextsState.currentContextId;
        if (!settingType) {
            settingType = ClientSettingTypeEnum.ContextUser;
        }
        await this._fetchService.put(
            `/contexts/${contextId}/interface-settings/global/`,
            {
                key,
                value,
                settingType,
                userId,
                roleName
            }
        );
    }

    public async changeTableLayoutSetting(
        key: string,
        value: string,
        settingType?: ClientSettingTypeEnum,
        userId?: string,
        roleName?: string): Promise<any> {

        const contextId = ContextsState.currentContextId;
        if (!settingType) {
            settingType = ClientSettingTypeEnum.ContextUser;
        }
        await this._fetchService.put(
            `/contexts/${contextId}/interface-settings/tablelayout/`,
            {
                key,
                value,
                settingType,
                userId,
                roleName
            }
        );

        SettingsState.contextSettings.tableLayouts = (await this.fetchSettings()).tableLayouts;
    }

    public async resetSettings(resetModel: IResetSettingsModel): Promise<boolean> {
        const contextId = ContextsState.currentContextId;
        let result = false;
        await this._fetchService.delete(
            `/contexts/${contextId}/interface-settings`,
            resetModel
        )
            .then(() => result = true)
            .catch(() => result = false);

        return result;
    }

    public async loadTiles() {
        const contextId = ContextsState.currentContextId;

        return await this._fetchService!
            .get<TileData[]>(`/contexts/${contextId}/settings/tiledata`);
    }
}
